import React from "react";
import styled from "styled-components";
import { device } from "../../helpers/style";
import { navigate } from "../../services";
import { DocMeta, docUrl, fixArabicText, getMetaValue } from "../../helpers";
import { Resize, Featured } from "../../models";
import prevImg from "./prev.png";
import nextImg from "./next.png";
import { AxiosResponse } from "axios";

const List = styled.div`
    position: relative;
    box-sizing: border-box;

    margin: 50px auto 50px auto;
    width: 88%;

    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: flex-start;
    text-align: center;

    @media ${device.phone} {
        display: none;
    }
`;

const Slide = styled.div<{
    zIndex: number;
    scaleX: number;
    scaleY: number;
}>`
    z-index: ${(p) => p.zIndex};
    transform-style: preserve-3d;
    transform: scale(${(p) => p.scaleX}, ${(p) => p.scaleY});
`;
export const Image = styled.img`
    cursor: pointer;
    width: 100%;
    border-radius: 4px;
`;
const Name = styled.div`
    font-size: 16px;
    font-weight: 500;
`;
const Authors = styled.div`
    font-size: 12px;
    font-weight: 500;
`;

const Nav = styled.img`
    cursor: pointer;
    position: absolute;
    z-index: 10;
    top: 40%;
    width: 24px;
    height: 40px;
`;
const Prev = styled(Nav)`
    right: -40px;
`;
const Next = styled(Nav)`
    left: -40px;
`;

// function rotateY(index: number) {
//     if (index === 1 || index === 2) {
//         return -2;
//     } else if (index === 4 || index === 5) {
//         return 2;
//     }

//     return 0;
// }

function zIndex(index: number) {
    if (index === 1 || index === 5) {
        return 1;
    } else if (index === 2 || index === 4) {
        return 2;
    }

    return 3;
}

function scaleX(index: number) {
    if (index === 3) {
        return 1.15;
    }

    return 0.9;
}

function scaleY(index: number) {
    if (index === 3) {
        return 1.1;
    }

    return 0.9;
}

export type ItemProps = {
    id: number;
    name: string;
    updated_at: string;
    doc_meta: DocMeta[];
};
function Item({ item, index }: { item: ItemProps; index: number }) {
    const { name, doc_meta } = item;
    const src = Resize.url("GET", `book/cover/${item.id}/440`);

    return (
        <Slide
            zIndex={zIndex(index)}
            scaleX={scaleX(index)}
            scaleY={scaleY(index)}
            onClick={() => navigate.to(docUrl(item))}
        >
            <Image src={src} />
            <Name dangerouslySetInnerHTML={fixArabicText(name)} />
            <Authors>{getMetaValue(doc_meta, "authors")}</Authors>
        </Slide>
    );
}

const MAX_ITEMS = 5;
const ANIM_DELAY = 10 * 1000;

type Props = {
    show: boolean;
    onFetch: (ids: number[]) => void;
};
type State = {
    offset: number;
    list: ItemProps[];
    stats: {
        offset: number;
        total: number;
    };
};

export default class Slider extends React.Component<Props, State> {
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private loading = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            offset: 0,
            list: [],
            stats: {
                offset: 0,
                total: 0,
            },
        };
    }

    async componentDidMount() {
        this.startAnim();
        await this.fetch();
    }

    componentWillUnmount() {
        this.stopAnim();
    }

    async fetch() {
        if (this.loading) {
            return;
        }

        this.loading = true;

        try {
            const params = [
                "includeMeta=true",
                "includesPages=false",
                "offset=0",
            ];

            const {
                data: { list, stats },
            }: AxiosResponse<
                Pick<State, "list" | "stats">
            > = await Featured.fetch("books", {
                url: params.join("&"),
            });
            this.setState({
                stats,
                list,
            });

            this.props.onFetch(list.map((i) => i.id));
            this.loading = false;
        } catch (e) {
            console.warn(e);
        }
    }

    started = false;
    startAnim = () => {
        if (this.started) {
            return;
        }

        this.started = true;
        this.intervalId = setInterval(this.next, ANIM_DELAY);
    };

    stopAnim() {
        this.started = false;
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    next = () => {
        const total = this.state.list.length;
        let { offset } = this.state;
        offset += 1;

        if (offset >= total) {
            this.setState({ offset: 0 });
        } else {
            this.setState({ offset });
        }
    };

    list = () => {
        let { list, offset } = this.state;
        const total = list.length;

        if (offset + MAX_ITEMS < total) {
            return list.slice(offset, offset + MAX_ITEMS);
        }

        let l = list.slice(offset);
        l = l.concat(list.slice(0, MAX_ITEMS - l.length));
        return l.concat(list.slice(0, MAX_ITEMS - l.length));
    };

    onPrev = () => {
        this.stopAnim();
        const { offset } = this.state;

        if (offset - 1 >= 0) {
            this.setState({ offset: offset - 1 }, () => {
                setTimeout(this.startAnim, ANIM_DELAY);
            });
        }
    };

    onNext = () => {
        this.stopAnim();
        const total = this.state.list.length;
        const { offset } = this.state;

        if (offset + 1 < total) {
            this.setState({ offset: offset + 1 }, () => {
                setTimeout(this.startAnim, ANIM_DELAY);
            });
        }
    };

    render() {
        if (!this.props.show) {
            return null;
        }

        return (
            <List>
                <Prev src={prevImg} onClick={this.onPrev} />
                <Next src={nextImg} onClick={this.onNext} />

                {this.list().map((item, index) => (
                    <Item
                        key={`slider-${item.id}`}
                        index={index + 1}
                        item={item}
                    />
                ))}
            </List>
        );
    }
}
